// src/pages/RTReportEditor.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { rtAPI } from '../services/api';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  Save, Send, Plus, Trash2, Sparkles, ChevronDown, ChevronUp,
  RefreshCw, X, Star, AlertTriangle, CheckCircle, ArrowLeft, UserX,
} from 'lucide-react';

// ── Styles partagés ───────────────────────────────────────────
const lbl = { display:'block', fontSize:'0.75rem', fontWeight:600, color:'var(--text-muted)', marginBottom:4, textTransform:'uppercase', letterSpacing:'0.04em' };
const inp = { width:'100%', padding:'8px 10px', border:'1.5px solid var(--border)', borderRadius:7, fontSize:'0.875rem', boxSizing:'border-box', fontFamily:'inherit', outline:'none', background:'var(--bg-elevated)', color:'var(--text-primary)' };
const sectionTitle = { fontSize:'0.8125rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10, display:'flex', alignItems:'center', gap:6 };

// ── Composant note étoiles ─────────────────────────────────────
function NoteSur10({ value, onChange }) {
  return (
    <div style={{ display:'flex', gap:4, alignItems:'center', flexWrap:'wrap' }}>
      {[1,2,3,4,5,6,7,8,9,10].map(n => (
        <button key={n} type="button" onClick={() => onChange(n)}
          style={{
            width:32, height:32, borderRadius:6, border:'1.5px solid',
            borderColor: n <= value ? (value >= 7 ? '#16A34A' : value >= 5 ? '#F59E0B' : '#DC2626') : 'var(--border)',
            background: n <= value ? (value >= 7 ? 'rgba(34,197,94,0.14)' : value >= 5 ? 'rgba(245,158,11,0.14)' : 'rgba(239,68,68,0.14)') : 'var(--bg-elevated)',
            color: n <= value ? (value >= 7 ? '#16A34A' : value >= 5 ? '#F59E0B' : '#DC2626') : 'var(--text-muted)',
            fontWeight:700, fontSize:'0.8125rem', cursor:'pointer',
          }}>
          {n}
        </button>
      ))}
      {value && <span style={{ marginLeft:6, fontWeight:800, fontSize:'1rem', color: value >= 7 ? '#16A34A' : value >= 5 ? '#F59E0B' : '#DC2626' }}>{value}/10</span>}
    </div>
  );
}

// ── Tableau dynamique soft skills ──────────────────────────────
function SoftTable({ rows, onChange }) {
  const update = (i, field, val) => onChange(rows.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  const add    = () => onChange([...rows, { critere: '', note_sur_10: null, commentaire: '' }]);
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'2fr 140px 2fr auto', gap:6, marginBottom:6, padding:'0 4px' }}>
        {['Critère / Soft skill', 'Note /10', 'Commentaire', ''].map(h => (
          <div key={h} style={{ ...lbl, marginBottom:0 }}>{h}</div>
        ))}
      </div>
      {rows.map((row, i) => (
        <div key={i} style={{ display:'grid', gridTemplateColumns:'2fr 140px 2fr auto', gap:6, marginBottom:6, alignItems:'center' }}>
          <input style={inp} placeholder="ex: Communication, Leadership…" value={row.critere}
            onChange={e => update(i, 'critere', e.target.value)}/>
          <div>
            <select style={{ ...inp, width:'100%' }} value={row.note_sur_10 || ''} onChange={e => update(i, 'note_sur_10', e.target.value ? Number(e.target.value) : null)}>
              <option value="">—</option>
              {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}/10</option>)}
            </select>
          </div>
          <input style={inp} placeholder="Observation…" value={row.commentaire}
            onChange={e => update(i, 'commentaire', e.target.value)}/>
          <button type="button" className="btn btn-ghost btn-icon btn-sm" style={{ color:'#DC2626' }} onClick={() => remove(i)}><Trash2 size={13}/></button>
        </div>
      ))}
      <button type="button" className="btn btn-ghost btn-sm" onClick={add} style={{ marginTop:4 }}><Plus size={13}/> Ajouter un critère</button>
    </div>
  );
}

// ── Tableau dynamique hard skills ──────────────────────────────
function HardTable({ rows, onChange }) {
  const update = (i, field, val) => onChange(rows.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  const add    = () => onChange([...rows, { competence: '', demandee: true, evaluee: false, note_sur_10: null, commentaire: '' }]);
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'2fr 70px 70px 120px 2fr auto', gap:6, marginBottom:6, padding:'0 4px' }}>
        {['Compétence', 'AO', 'Évaluée', 'Note /10', 'Commentaire', ''].map(h => (
          <div key={h} style={{ ...lbl, marginBottom:0 }}>{h}</div>
        ))}
      </div>
      {rows.map((row, i) => (
        <div key={i} style={{ display:'grid', gridTemplateColumns:'2fr 70px 70px 120px 2fr auto', gap:6, marginBottom:6, alignItems:'center' }}>
          <input style={inp} placeholder="ex: React, Java, AWS…" value={row.competence}
            onChange={e => update(i, 'competence', e.target.value)}/>
          <div style={{ display:'flex', justifyContent:'center' }}>
            <input type="checkbox" checked={!!row.demandee} onChange={e => update(i, 'demandee', e.target.checked)}
              style={{ width:16, height:16, cursor:'pointer' }}/>
          </div>
          <div style={{ display:'flex', justifyContent:'center' }}>
            <input type="checkbox" checked={!!row.evaluee} onChange={e => update(i, 'evaluee', e.target.checked)}
              style={{ width:16, height:16, cursor:'pointer' }}/>
          </div>
          <select style={{ ...inp }} value={row.note_sur_10 || ''} onChange={e => update(i, 'note_sur_10', e.target.value ? Number(e.target.value) : null)}>
            <option value="">—</option>
            {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}/10</option>)}
          </select>
          <input style={inp} placeholder="Observation…" value={row.commentaire}
            onChange={e => update(i, 'commentaire', e.target.value)}/>
          <button type="button" className="btn btn-ghost btn-icon btn-sm" style={{ color:'#DC2626' }} onClick={() => remove(i)}><Trash2 size={13}/></button>
        </div>
      ))}
      <button type="button" className="btn btn-ghost btn-sm" onClick={add} style={{ marginTop:4 }}><Plus size={13}/> Ajouter une compétence</button>
    </div>
  );
}

// ── Panneau questions IA (droite) ──────────────────────────────
function AIQuestionsPanel({ aoId, candidateId, questions, onQuestionsChange }) {
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await rtAPI.generateQuestions(aoId, candidateId);
      onQuestionsChange(res.data?.questions || []);
      toast.success('Questions générées');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur génération IA');
    } finally { setGenerating(false); }
  };

  const update = (i, field, val) => {
    const next = [...questions];
    next[i] = { ...next[i], [field]: val };
    onQuestionsChange(next);
  };
  const add    = () => onQuestionsChange([...questions, { question: '', reponse_attendue: '', reponse_candidat: '' }]);
  const remove = (i) => onQuestionsChange(questions.filter((_, idx) => idx !== i));

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', gap:12 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={sectionTitle}><Sparkles size={14} color="#8B5CF6"/> Assistant questions IA</div>
        <button className="btn btn-secondary btn-sm" onClick={generate} disabled={generating} style={{ borderColor:'#A78BFA', color:'#A78BFA' }}>
          {generating ? <><div className="spinner" style={{ width:12, height:12 }}/> Génération…</> : <><RefreshCw size={13}/> Générer</>}
        </button>
      </div>

      {questions.length === 0 ? (
        <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:10, color:'var(--text-muted)', fontSize:'0.875rem', padding:24, border:'2px dashed var(--border)', borderRadius:10 }}>
          <Sparkles size={28} style={{ opacity:0.3 }}/>
          <div style={{ fontWeight:600 }}>Cliquez sur Générer</div>
          <div style={{ textAlign:'center', fontSize:'0.8125rem' }}>L'IA va créer 10+ questions techniques calibrées sur l'AO et le CV du candidat, avec les réponses attendues.</div>
        </div>
      ) : (
        <div style={{ flex:1, overflowY:'auto', display:'flex', flexDirection:'column', gap:14 }}>
          {questions.map((q, i) => (
            <div key={i} style={{ border:'1.5px solid var(--border)', borderRadius:8, padding:'12px 14px', background:'var(--bg-card)' }}>
              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:8 }}>
                <span style={{ whiteSpace:'nowrap', fontSize:'0.75rem', fontWeight:700, color:'#A78BFA', background:'rgba(139,92,246,0.14)', padding:'2px 8px', borderRadius:20 }}>Q{i + 1}</span>
                <button type="button" className="btn btn-ghost btn-icon btn-sm" style={{ color:'#DC2626' }} onClick={() => remove(i)}><X size={14}/></button>
              </div>
              <div style={{ marginBottom:8 }}>
                <label style={lbl}>Question</label>
                <textarea style={{ ...inp, minHeight:50, resize:'vertical' }} value={q.question}
                  onChange={e => update(i, 'question', e.target.value)}/>
              </div>
              <div style={{ marginBottom:8, background:'rgba(34,197,94,0.10)', borderRadius:6, padding:'8px 10px' }}>
                <label style={{ ...lbl, color:'#16A34A' }}>Réponse attendue (référence)</label>
                <textarea style={{ ...inp, minHeight:50, resize:'vertical', background:'transparent', border:'1.5px solid #BBF7D0' }} value={q.reponse_attendue}
                  onChange={e => update(i, 'reponse_attendue', e.target.value)}/>
              </div>
              <div style={{ background:'rgba(59,130,246,0.06)', borderRadius:6, padding:'8px 10px' }}>
                <label style={{ ...lbl, color:'#3B82F6' }}>Réponse du candidat</label>
                <textarea style={{ ...inp, minHeight:50, resize:'vertical', background:'transparent', border:'1.5px solid rgba(59,130,246,0.22)' }} value={q.reponse_candidat}
                  onChange={e => update(i, 'reponse_candidat', e.target.value)}/>
              </div>
            </div>
          ))}
          <button type="button" className="btn btn-ghost btn-sm" onClick={add}><Plus size={13}/> Ajouter une question</button>
        </div>
      )}
    </div>
  );
}


// ── Page principale ────────────────────────────────────────────
export default function RTReportEditor() {
  const { aoId, candidateId } = useParams();
  const navigate = useNavigate();

  const [ao,        setAo]        = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [reports,   setReports]   = useState([]);
  const [activeType,setActiveType]= useState('SOFT_SKILLS'); // SOFT_SKILLS | HARD_SKILLS
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);

  // Champs formulaire
  const [note,       setNote]       = useState(null);
  const [commentaire,setCommentaire]= useState('');
  const [tableRows,  setTableRows]  = useState([]);
  const [questions,  setQuestions]  = useState([]);
  const [reportId,     setReportId]     = useState(null);
  const [submitted,    setSubmitted]    = useState(false);
  const [disqualifying,setDisqualifying]= useState(false);

  const activeReport = reports.find(r => r.report_type === activeType);

  const loadFromReport = useCallback((report) => {
    if (!report) {
      setNote(null); setCommentaire(''); setTableRows([]); setQuestions([]); setReportId(null); setSubmitted(false);
      return;
    }
    setNote(report.note_sur_10 ? Number(report.note_sur_10) : null);
    setCommentaire(report.commentaire || '');
    setTableRows(report.table_data || []);
    setQuestions(report.questions_ia || []);
    setReportId(report.id);
    setSubmitted(report.status === 'SOUMIS');
  }, []);

  useEffect(() => { loadFromReport(activeReport); }, [activeType, activeReport, loadFromReport]);

  // Pré-remplir le tableau avec les compétences de l'AO si aucun rapport existant
  useEffect(() => {
    if (!ao || activeReport) return;
    if (activeType === 'SOFT_SKILLS') {
      const skills = ao.soft_skills || [];
      if (skills.length > 0) {
        setTableRows(skills.map(s => ({ critere: s, note_sur_10: null, commentaire: '' })));
      }
    } else {
      const skills = ao.tech_skills || [];
      if (skills.length > 0) {
        setTableRows(skills.map(s => ({ competence: s, demandee: true, evaluee: false, note_sur_10: null, commentaire: '' })));
      }
    }
  }, [ao, activeType, activeReport]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [aoRes, candRes, reportsRes] = await Promise.all([
        api.get(`/ao-kanban/${aoId}`).catch(() => ({ data: null })),
        api.get(`/candidates/${candidateId}`).catch(() => ({ data: null })),
        rtAPI.getReportsForCandidateOnAO(aoId, candidateId),
      ]);
      setAo(aoRes.data);
      setCandidate(candRes.data);
      setReports(reportsRes.data || []);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, [aoId, candidateId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const save = async (submit = false) => {
    setSaving(true);
    try {
      const payload = {
        note_sur_10: note,
        commentaire,
        table_data: tableRows,
        questions_ia: questions,
      };
      let saved;
      if (reportId) {
        saved = await rtAPI.updateReport(reportId, payload);
      } else {
        saved = await rtAPI.createReport({ ao_id: aoId, candidate_id: candidateId, report_type: activeType, ...payload });
        setReportId(saved.data.id);
      }
      if (submit) {
        if (!note) { toast.error('Note sur 10 obligatoire pour soumettre'); setSaving(false); return; }
        await rtAPI.submitReport(saved.data?.id || reportId);
        setSubmitted(true);
        toast.success('Rapport soumis — le TA et l\'IA ont été notifiés');
      } else {
        toast.success('Brouillon sauvegardé');
      }
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur sauvegarde');
    } finally { setSaving(false); }
  };

  const handleDisqualify = async () => {
    if (!reportId) { toast.error('Sauvegardez d\'abord le rapport'); return; }
    if (!window.confirm('Disqualifier ce candidat pour cet AO ? Son statut pipeline passera à "Non qualifié".')) return;
    setDisqualifying(true);
    try {
      await rtAPI.disqualifyReport(reportId);
      toast.success('Candidat disqualifié');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur disqualification');
    } finally { setDisqualifying(false); }
  };

  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', padding:64, gap:12, color:'var(--text-muted)' }}>
      <div className="spinner"/> Chargement…
    </div>
  );

  const isHard = activeType === 'HARD_SKILLS';

  return (
    <div style={{ height:'calc(100vh - 64px)', display:'flex', flexDirection:'column', gap:0 }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom:12 }}>
        <div className="page-header-left">
          <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)} style={{ marginBottom:6, alignSelf:'flex-start' }}>
            <ArrowLeft size={14}/> Retour à l'AO
          </button>
          <h1 style={{ fontSize:'1.125rem' }}>
            Rapport RT — {ao?.job_title || 'AO'}{ao?.client ? ` · ${ao.client}` : ''}
          </h1>
          <p>{candidate?.full_name || 'Candidat'}{candidate?.title ? ` — ${candidate.title}` : ''}</p>
        </div>
        <div className="page-header-actions">
          {/* Toggle type */}
          <div style={{ display:'flex', border:'1.5px solid var(--border)', borderRadius:8, overflow:'hidden' }}>
            {['SOFT_SKILLS','HARD_SKILLS'].map(t => (
              <button key={t} type="button" onClick={() => setActiveType(t)}
                style={{ padding:'6px 14px', background: activeType===t ? '#3B82F6' : 'var(--bg-elevated)', color: activeType===t ? 'white' : 'var(--text-muted)', border:'none', cursor:'pointer', fontWeight:600, fontSize:'0.8125rem' }}>
                {t === 'SOFT_SKILLS' ? 'Soft Skills' : 'Hard Skills'}
                {reports.find(r => r.report_type === t) && (
                  <span style={{ whiteSpace: 'nowrap', marginLeft:6, fontSize:'0.6875rem', background: activeType===t ? 'rgba(255,255,255,0.3)' : 'rgba(59,130,246,0.06)', color: activeType===t ? 'white' : '#3B82F6', borderRadius:10, padding:'1px 6px' }}>
                    {reports.find(r => r.report_type === t)?.status === 'SOUMIS' ? '✓' : '…'}
                  </span>
                )}
              </button>
            ))}
          </div>
          <button className="btn btn-secondary" disabled={saving || submitted} onClick={() => save(false)}>
            {saving ? <div className="spinner" style={{ width:13, height:13 }}/> : <Save size={14}/>} Sauvegarder
          </button>
          <button className="btn btn-primary" disabled={saving || submitted} onClick={() => save(true)}>
            {saving ? <div className="spinner" style={{ width:13, height:13 }}/> : <Send size={14}/>} Soumettre
          </button>
          {reportId && (
            <button
              className="btn"
              disabled={disqualifying}
              onClick={handleDisqualify}
              style={{ background:'rgba(220,38,38,0.10)', color:'#DC2626', border:'1.5px solid rgba(220,38,38,0.3)' }}
            >
              {disqualifying ? <div className="spinner" style={{ width:13, height:13 }}/> : <UserX size={14}/>} Disqualifier
            </button>
          )}
        </div>
      </div>

      {submitted && (
        <div style={{ background:'rgba(34,197,94,0.10)', border:'1px solid rgba(34,197,94,0.22)', borderRadius:8, padding:'10px 16px', marginBottom:12, display:'flex', alignItems:'center', gap:8, color:'#16A34A', fontWeight:600 }}>
          <CheckCircle size={16}/> Rapport {activeType === 'SOFT_SKILLS' ? 'Soft Skills' : 'Hard Skills'} soumis — lecture seule
        </div>
      )}

      {/* Contenu — split view si Hard Skills */}
      <div style={{ flex:1, overflow:'hidden', display:'grid', gridTemplateColumns: isHard ? '1fr 1fr' : '1fr', gap:16 }}>
        {/* Panneau gauche — formulaire rapport */}
        <div style={{ overflowY:'auto', display:'flex', flexDirection:'column', gap:16 }}>

          {/* Note globale */}
          <div className="card" style={{ padding:'16px 20px' }}>
            <div style={sectionTitle}><Star size={14} color="#F59E0B"/> Note globale</div>
            <NoteSur10 value={note} onChange={submitted ? undefined : setNote}/>
          </div>

          {/* Tableau */}
          <div className="card" style={{ padding:'16px 20px' }}>
            <div style={sectionTitle}>
              {isHard ? '🛠 Compétences techniques' : '🧠 Critères soft skills'}
            </div>
            {isHard
              ? <HardTable rows={tableRows} onChange={submitted ? undefined : setTableRows}/>
              : <SoftTable rows={tableRows} onChange={submitted ? undefined : setTableRows}/>
            }
          </div>

          {/* Commentaire */}
          <div className="card" style={{ padding:'16px 20px' }}>
            <div style={sectionTitle}>Synthèse / Commentaire</div>
            <textarea disabled={submitted} style={{ ...inp, minHeight:100, resize:'vertical' }}
              placeholder="Points forts, points faibles, recommandation globale…"
              value={commentaire} onChange={e => setCommentaire(e.target.value)}/>
          </div>
        </div>

        {/* Panneau droit — Questions IA (Hard Skills uniquement) */}
        {isHard && (
          <div className="card" style={{ padding:'16px 20px', overflowY:'auto', display:'flex', flexDirection:'column' }}>
            <AIQuestionsPanel
              aoId={aoId}
              candidateId={candidateId}
              questions={questions}
              onQuestionsChange={submitted ? undefined : setQuestions}
            />
          </div>
        )}
      </div>
    </div>
  );
}
